import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

import { getChatHistory, getModelList, getSessionList, GetSessionListRes } from '@/api/agent'

export interface ChatAttachment {
  name: string
  url: string
  type: 'image' | 'file'
  mimeType: string
}

export interface Message {
  id: number
  role: string
  content: string
  attachments?: ChatAttachment[]
  skillName?: string
  skillAvatar?: string
}

export interface Model {
  name: string
  modelId: string
  capabilities: string[]
  modelIcon?: string
}

interface ChatState {
  // 侧边栏状态
  isSidebarOpen: boolean
  setIsSidebarOpen: (isOpen: boolean) => void

  // 登录弹窗状态
  isLoginModalOpen: boolean
  setIsLoginModalOpen: (isOpen: boolean) => void

  // 资源预览状态
  isResourcePreviewOpen: boolean
  setIsResourcePreviewOpen: (isOpen: boolean) => void
  selectedResourceIndex: number
  setSelectedResourceIndex: (index: number) => void

  // HTML预览状态
  isHtmlPreviewOpen: boolean
  setIsHtmlPreviewOpen: (isOpen: boolean) => void
  htmlPreviewCode: string
  setHtmlPreviewCode: (code: string) => void
  htmlPreviewLanguage: string
  setHtmlPreviewLanguage: (language: string) => void
  htmlPreviewInitialTab: 'preview' | 'code'
  setHtmlPreviewInitialTab: (tab: 'preview' | 'code') => void

  // 模型相关
  availableModels: Model[]
  selectedModelIds: string[]
  selectedSkillName: string
  selectedSkillDisplayName: string
  selectedSkillAvatar: string
  selectedSkillModelId: string
  newChatTrigger: number
  fetchAvailableModels: () => Promise<void>
  toggleModelSelection: (modelId: string) => void
  setSelectedModelIds: (ids: string[]) => void
  setSelectedSkillName: (skillName: string) => void
  setSelectedSkillDisplayName: (displayName: string) => void
  setSelectedSkillAvatar: (avatar: string) => void
  setSelectedSkillModelId: (modelId: string) => void
  triggerNewChat: () => void

  // 消息相关
  messages: Message[]
  isHistoryLoading: boolean
  setMessages: (messages: Message[] | ((prev: Message[]) => Message[])) => void
  clearMessages: () => void
  scrollToBottomTrigger: number
  triggerScrollToBottom: () => void
  // 流式状态：当前正在接收流式回复的消息 id 与对应会话 id（用于显示 loading、避免 initSession 覆盖）
  streamingMessageId: number | null
  streamingSessionId: string | null
  setStreaming: (messageId: number | null, sessionId: string | null) => void

  // 会话相关
  sessionList: GetSessionListRes['data']
  setSessionList: (list: GetSessionListRes['data']) => void
  sessionTitleMap: Record<string, string>
  setSessionTitle: (sessionId: string, title: string) => void
  fetchSessions: () => Promise<void>
  clearSessions: () => void

  // 初始化会话（加载历史记录和对应模型）
  initSession: (sessionId?: string) => Promise<void>
}

export const useChatStore = create<ChatState>()(
  devtools(
    immer((set, get) => ({
      isSidebarOpen: true,
      setIsSidebarOpen: (isOpen) =>
        set((state) => {
          state.isSidebarOpen = isOpen
        }),

      isLoginModalOpen: false,
      setIsLoginModalOpen: (isOpen) =>
        set((state) => {
          state.isLoginModalOpen = isOpen
        }),

      isResourcePreviewOpen: false,
      setIsResourcePreviewOpen: (isOpen) =>
        set((state) => {
          state.isResourcePreviewOpen = isOpen
          if (isOpen) state.isHtmlPreviewOpen = false
        }),
      selectedResourceIndex: 0,
      setSelectedResourceIndex: (index) =>
        set((state) => {
          state.selectedResourceIndex = index
        }),

      isHtmlPreviewOpen: false,
      setIsHtmlPreviewOpen: (isOpen) =>
        set((state) => {
          state.isHtmlPreviewOpen = isOpen
          if (isOpen) state.isResourcePreviewOpen = false
        }),
      htmlPreviewCode: '',
      setHtmlPreviewCode: (code) =>
        set((state) => {
          state.htmlPreviewCode = code
        }),
      htmlPreviewLanguage: 'html',
      setHtmlPreviewLanguage: (language) =>
        set((state) => {
          state.htmlPreviewLanguage = language || 'plaintext'
        }),
      htmlPreviewInitialTab: 'preview',
      setHtmlPreviewInitialTab: (tab) =>
        set((state) => {
          state.htmlPreviewInitialTab = tab
        }),

      availableModels: [],
      selectedModelIds: [],
      selectedSkillName: '',
      selectedSkillDisplayName: '',
      selectedSkillAvatar: '',
      selectedSkillModelId: '',
      newChatTrigger: 0,
      fetchAvailableModels: async () => {
        try {
          const res = await getModelList()
          const list = res.data || []
          const formattedList = list.map((item) => ({
            name: item.name,
            modelId: item.modelId,
            capabilities: item.capabilities || [],
            modelIcon: item.modelIcon
          }))
          set((state) => {
            state.availableModels = formattedList
            // 如果当前没有选中的模型，默认选中第一个（/v2/agent/models 第一项）
            if (state.selectedModelIds.length === 0 && formattedList.length > 0) {
              state.selectedModelIds = [formattedList[0].modelId]
            }
          })
        } catch (error) {
          console.error('Failed to fetch model list:', error)
        }
      },
      toggleModelSelection: (modelId) => {
        set((state) => {
          state.selectedModelIds = [modelId]
        })
      },
      setSelectedModelIds: (ids) =>
        set((state) => {
          state.selectedModelIds = ids
        }),
      setSelectedSkillName: (skillName) =>
        set((state) => {
          state.selectedSkillName = skillName
        }),
      setSelectedSkillDisplayName: (displayName) =>
        set((state) => {
          state.selectedSkillDisplayName = displayName
        }),
      setSelectedSkillAvatar: (avatar) =>
        set((state) => {
          state.selectedSkillAvatar = avatar
        }),
      setSelectedSkillModelId: (modelId) =>
        set((state) => {
          state.selectedSkillModelId = modelId
        }),
      triggerNewChat: () =>
        set((state) => {
          state.newChatTrigger += 1
          state.selectedSkillName = ''
          state.selectedSkillDisplayName = ''
          state.selectedSkillAvatar = ''
          state.selectedSkillModelId = ''
        }),

      messages: [],
      isHistoryLoading: false,
      setMessages: (updater) => {
        set((state) => {
          if (typeof updater === 'function') {
            state.messages = updater(state.messages)
          } else {
            state.messages = updater
          }
        })
      },
      clearMessages: () =>
        set((state) => {
          state.messages = []
        }),

      scrollToBottomTrigger: 0,
      triggerScrollToBottom: () =>
        set((state) => {
          state.scrollToBottomTrigger += 1
        }),

      streamingMessageId: null,
      streamingSessionId: null,
      setStreaming: (messageId, sessionId) =>
        set((state) => {
          state.streamingMessageId = messageId
          state.streamingSessionId = sessionId
        }),

      sessionList: [],
      setSessionList: (list) =>
        set((state) => {
          state.sessionList = list
        }),
      sessionTitleMap: {},
      setSessionTitle: (sessionId, title) =>
        set((state) => {
          state.sessionTitleMap[sessionId] = title
        }),
      fetchSessions: async () => {
        try {
          const res = await getSessionList()
          const list = res.data || []
          set((state) => {
            state.sessionList = list
          })
        } catch (error) {
          console.error('Failed to fetch sessions:', error)
        }
      },
      clearSessions: () =>
        set((state) => {
          state.sessionList = []
        }),

      initSession: async (sessionId) => {
        if (sessionId) {
          set((state) => {
            state.isHistoryLoading = true
          })
          // 1. 获取聊天历史（v2 接口：data 为 role/parts/created_at 结构）
          try {
            const res = await getChatHistory(sessionId)
            const list = res.data || []
            const formattedList: Message[] = list.map((msg, index) => {
              const content =
                msg.parts
                  ?.filter((p) => p.content_type === 'text/plain' && p.content)
                  .map((p) => p.content)
                  .filter(Boolean)
                  .join('\n') || ''

              let skillName = ''
              let skillAvatar = ''
              const configPart = msg.parts?.find((p) => p.content_type === 'application/json' && p.content)
              if (configPart?.content) {
                try {
                  const parsed = JSON.parse(configPart.content) as {
                    extra_env?: {
                      skill_name?: unknown
                      skill_avatar?: unknown
                    }
                  }
                  if (typeof parsed?.extra_env?.skill_name === 'string') {
                    skillName = parsed.extra_env.skill_name
                  }
                  if (typeof parsed?.extra_env?.skill_avatar === 'string') {
                    skillAvatar = parsed.extra_env.skill_avatar
                  }
                } catch {
                  // ignore invalid config payload
                }
              }

              // 提取图片类型 parts 作为附件
              const imageParts = msg.parts?.filter((p) => p.content_type?.startsWith('image/') && p.content_url) || []
              const attachments: ChatAttachment[] = imageParts.map((p) => ({
                name: p.name || p.content_url?.split('/').pop() || 'image',
                url: p.content_url!,
                type: 'image',
                mimeType: p.content_type
              }))

              return {
                id: index + 1,
                role: msg.role === 'agent' ? 'assistant' : msg.role,
                content,
                ...(attachments.length > 0 ? { attachments } : {}),
                ...(skillName ? { skillName } : {}),
                ...(skillAvatar ? { skillAvatar } : {})
              }
            })
            set((state) => {
              // 若当前会话是客户端生成的且正在流式，且历史为空，则不覆盖已显示的 messages
              if (formattedList.length === 0 && get().streamingSessionId === sessionId) {
                return
              }
              state.messages = formattedList
            })
          } catch (error) {
            console.error('Failed to load chat history:', error)
            // 新会话可能 404，若正在流式则保留当前 messages，不覆盖
            if (get().streamingSessionId === sessionId) return
            set((state) => {
              state.messages = []
            })
          } finally {
            set((state) => {
              state.isHistoryLoading = false
            })
          }

          // 2. 获取当前会话对应的模型（优先用侧边栏已拉取的 sessionList，避免重复请求）
          let resolvedModelId: string | undefined
          const fromList = get().sessionList?.find((s) => s.sessionId === sessionId)
          if (fromList?.modelId) {
            resolvedModelId = fromList.modelId
          } else {
            try {
              const res = await getSessionList()
              const session = res.data?.find((s) => s.sessionId === sessionId)
              if (session?.modelId) {
                resolvedModelId = session.modelId
              }
            } catch (error) {
              console.error('Failed to fetch session list for modelId:', error)
            }
          }

          // 校验 modelId 是否在可用列表中，不存在则回退到第一个模型
          const models = get().availableModels
          const isValid = resolvedModelId && models.some((m) => m.modelId === resolvedModelId)
          set((state) => {
            state.selectedModelIds = [isValid ? resolvedModelId! : (models[0]?.modelId ?? '')]
          })
        } else {
          // 新会话：清空消息 + 重置为第一个模型
          const firstModelId = get().availableModels[0]?.modelId ?? ''
          set((state) => {
            state.isHistoryLoading = false
            state.messages = []
            state.selectedModelIds = firstModelId ? [firstModelId] : []
            state.selectedSkillName = ''
            state.selectedSkillDisplayName = ''
            state.selectedSkillAvatar = ''
            state.selectedSkillModelId = ''
          })
        }
      }
    })),
    { name: 'chat-store' }
  )
)
